/**
 * Onboarding step 6 (ai-demo) — starter chip prompts.
 *
 * The original 63-chip curated table was generated by a subagent during the
 * V7 session that wasn't captured in the parent transcript. Reconstructed
 * here as a smaller seed: 3 chips per (goal × top struggle) cell when we
 * have one written, falling back to GENERIC_STARTERS otherwise.
 *
 * Shape:
 *   getStartersFor(goal, topStruggle) → string[]
 *
 * If a (goal × struggle) cell exists, return its 3 chips. If not, return
 * GENERIC_STARTERS (3 universal first-person prompts that work without
 * context).
 *
 * Chip voice: first-person, ≤ 18 words, raw + honest, not preachy.
 * Mason 2026-05-17 lock — every chip should sound like the USER asking,
 * not a category label.
 */

// Universal fallback — works for any goal × struggle combination.
export const GENERIC_STARTERS = [
  "I want to grow but I keep stalling — what's one small thing I can do this week?",
  'How do I make my time in the Word actually stick instead of bouncing off?',
  'What would you say to someone who feels stuck in their faith right now?',
]

// Per-(goal × struggle) chips. Only cells with meaningful differentiation are
// filled — the rest fall through to GENERIC_STARTERS via getStartersFor.
export const DEMO_STARTERS = {
  grow_closer: {
    staying_consistent: [
      'I keep starting strong and falling off by week 3 — how do I break the cycle?',
      "What's a tiny daily rhythm that compounds without feeling like a chore?",
      "How do I keep going when I miss a day or two and feel like I've failed?",
    ],
    prayer_life: [
      'My prayers feel like a script. How do I make them honest again?',
      "I forget to pray until I'm in trouble — how do I build the habit before the crisis?",
      'What does a real, unforced 10-minute prayer time actually look like?',
    ],
    scripture_comprehension: [
      'I open my Bible and most of it bounces off. Where do I start?',
      'How do I read a chapter and actually walk away with something?',
      "What's the difference between reading the Bible and studying it?",
    ],
    daily_walk: [
      "What does 'walking with God' actually mean on a Tuesday morning?",
      'How do I notice God in the boring parts of my day?',
      'I want intimacy with God, not just information about Him. Where do I start?',
    ],
    doubt: [
      "Some weeks I'm not sure I believe any of this — is that normal?",
      "How do I talk to God when I'm not sure He's listening?",
      "What does honest faith look like when doubt won't go away?",
    ],
  },
  know_what_believe: {
    doubt: [
      'Someone keeps pushing back on what I believe and I freeze. Help me get sharper.',
      "What's a non-defensive way to wrestle with hard questions about Christianity?",
      'How do I know what I actually believe vs. what I was raised to believe?',
    ],
    defending_faith: [
      'How do I respond when someone says all religions are basically the same?',
      'What do I say when a friend asks why a good God allows suffering?',
      'How do I defend my faith without sounding combative or rehearsed?',
    ],
    scripture_comprehension: [
      'How do I tell when a Bible passage is meant literally vs. figuratively?',
      "What's the difference between an Old Testament command and a New Testament one for me today?",
      'How do I read confusing parts of the Bible without giving up?',
    ],
    prayer_life: [
      'Why do my prayers feel so theological and stiff? How do I make them real?',
      "What's the relationship between believing the right things and praying well?",
      "How do I know if I'm praying correctly?",
    ],
    staying_consistent: [
      "I learn a lot but don't apply it — how do I close that gap?",
      'How do I keep studying when I feel like I already know enough?',
      "What's one rhythm that keeps theology from staying just in my head?",
    ],
  },
  lead_others: {
    sermon_prep: [
      'Walk me through how to study a passage for a sermon when I have 4 hours total.',
      'How do I land a message so people leave with one thing to actually do?',
      "What's the difference between a Bible lesson and a sermon that moves people?",
    ],
    daily_walk: [
      'How do I lead others well when my own walk feels dry?',
      "What's the rhythm of a pastor whose own soul is fed?",
      'How do I keep ministry from becoming a substitute for my own prayer life?',
    ],
    prayer_life: [
      "How do I pray for people I'm shepherding without it becoming a list?",
      "What does intercession look like for someone I'm discipling closely?",
      'How do I model honest prayer to people who watch me lead?',
    ],
    defending_faith: [
      'How do I prepare my people for the hard questions their kids will ask?',
      "What's a gentle way to push back when someone in my small group says something off?",
      'How do I teach apologetics without making my group feel like a debate club?',
    ],
    staying_consistent: [
      "How do I keep my own rhythm when I'm pouring out for everyone else?",
      'What does sustainable ministry look like for a solo pastor / leader?',
      'How do I avoid burning out while still showing up for the people I lead?',
    ],
  },
}

/**
 * Get the 3 starter chips for a (goal × topStruggle) pair.
 * Falls back to GENERIC_STARTERS if no specific cell exists.
 */
export function getStartersFor(goal, topStruggle) {
  const cell = DEMO_STARTERS[goal]?.[topStruggle]
  return cell || GENERIC_STARTERS
}
